import { useRef } from 'react';
import { Button, Table, Tabs, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import * as XLSX from 'xlsx';
import { AlertTriangle, Banknote, CheckCircle2, FileQuestion, FileUp, Landmark, Percent, Sparkles } from 'lucide-react';
import Layout from '../../Layout';
import { sampleBank, samplePayoo } from './ktDemo';
import { ktApi, useKt } from './ktStore';
import {
  dmy, fmtMoney, matchBank, matchPayoo, MATCH_LABEL, parseBank, parsePayoo, PAY_METHOD,
  type BankMatch, type Bill, type Payment, type PayooMatch,
} from './ktLogic';
import { DemoBar, Kpi, MatchTag, PageHeader, Panel } from './ktUi';

/** Đọc sheet đầu của file Excel thành các dòng theo tên cột */
async function readSheet(file: File) {
  const wb = XLSX.read(await file.arrayBuffer());
  return XLSX.utils.sheet_to_json<Record<string, unknown>>(wb.Sheets[wb.SheetNames[0]], { raw: false });
}

function FileButtons({ onSample, onRows, onClear, hasData, columns }: {
  onSample: () => void; onRows: (rows: Record<string, unknown>[]) => number; onClear: () => void; hasData: boolean; columns: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button type="primary" icon={<Sparkles size={14} />} onClick={onSample}>Nạp file mẫu</Button>
      <Button icon={<FileUp size={14} />} onClick={() => input.current?.click()}>Chọn file Excel</Button>
      {hasData && <Button onClick={onClear}>Xóa dữ liệu</Button>}
      <input ref={input} type="file" accept=".xlsx,.xls,.csv" className="hidden" aria-label="Chọn file Excel đối soát"
        onChange={async e => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          try {
            const n = onRows(await readSheet(file));
            if (n) message.success(`Đã đọc ${n} dòng`); else message.warning(`Không đọc được dòng nào. File cần có cột: ${columns}`);
          } catch { message.error('Không đọc được file này'); }
        }} />
      <span className="text-xs text-gray-500">Cột cần có: {columns}</span>
    </div>
  );
}

const MissingList = ({ items, note }: { items: { b: Bill; p: Payment }[]; note: string }) => (
  <Panel title={`Có trên bill nhưng không có trong file (${items.length})`} icon={<AlertTriangle size={15} className="text-amber-600" />}>
    <div className="divide-y divide-gray-100 dark:divide-gray-800">
      {items.map(({ b, p }) => (
        <div key={p.id} className="px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
          <span>{b.no} · {b.customer} · {PAY_METHOD[p.method]} · {p.ref || <span className="text-rose-600">thiếu mã</span>}</span>
          <b>{fmtMoney(p.amount)}</b>
        </div>
      ))}
    </div>
    <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100 dark:border-gray-800">{note}</div>
  </Panel>
);

function PayooTab() {
  const s = useKt();
  const r = matchPayoo(s.payoo, s.bills);
  const count = (st: PayooMatch['status']) => r.matches.filter(m => m.status === st).length;
  const columns: ColumnsType<PayooMatch> = [
    { title: 'Mã giao dịch', key: 'ref', render: (_, m) => <span className="font-medium">{m.row.ref}</span> },
    { title: 'Ngày', key: 'd', width: 110, render: (_, m) => (m.row.date ? dmy(m.row.date) : '—') },
    { title: 'Bill, khách', key: 'b', render: (_, m) => (m.bill ? `${m.bill.no} · ${m.bill.customer}` : <span className="text-gray-400">Không tìm thấy</span>) },
    { title: 'Tiền giao dịch', key: 'g', align: 'right', width: 130, render: (_, m) => fmtMoney(m.row.gross) },
    { title: 'Phí POS (TK 641/635)', key: 'f', align: 'right', width: 160, render: (_, m) => fmtMoney(m.row.fee) },
    { title: 'Thực nhận (TK 112)', key: 'n', align: 'right', width: 150, render: (_, m) => <b>{fmtMoney(m.row.net)}</b> },
    { title: 'Kết quả', key: 's', width: 200, render: (_, m) => (
      <div><MatchTag status={m.status} label={MATCH_LABEL[m.status]} />{m.status === 'amount' && <div className="text-[11px] text-amber-700 mt-0.5">Chênh {fmtMoney(m.diff)} so với bill</div>}{m.status === 'dup' && <div className="text-[11px] text-rose-700 mt-0.5">Giao dịch đã khớp ở dòng khác, không ghi sổ lần hai</div>}</div>
    ) },
  ];
  return (
    <div className="flex flex-col gap-4">
      <FileButtons hasData={s.payoo.length > 0} columns="Mã giao dịch, Số tiền, Phí, Ngày"
        onSample={() => ktApi.setPayoo(samplePayoo(s))} onClear={() => ktApi.setPayoo([])}
        onRows={rows => { const parsed = parsePayoo(rows); if (parsed.length) ktApi.setPayoo(parsed); return parsed.length; }} />
      {s.payoo.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <Kpi icon={<CheckCircle2 size={20} />} tone="bg-emerald-50 text-emerald-600" label="Khớp" value={`${count('matched')}/${r.matches.length}`} hint={`${count('amount')} lệch tiền · ${count('no_bill')} không có bill${count('dup') ? ` · ${count('dup')} trùng` : ''}`} />
            <Kpi icon={<FileQuestion size={20} />} tone="bg-amber-50 text-amber-600" label="Bill chưa có trong file" value={r.missing.length} hint="Chờ file đợt sau hoặc cần kiểm tra" />
            <Kpi icon={<Banknote size={20} />} tone="bg-sky-50 text-sky-600" label="Thực nhận về ngân hàng" value={fmtMoney(r.net)} hint="TK 112" />
            <Kpi icon={<Percent size={20} />} tone="bg-rose-50 text-rose-600" label="Phí POS, trả góp" value={fmtMoney(r.fee)} hint="TK 641/635, tách theo từng giao dịch" />
          </div>
          <Panel title={`Kết quả khớp (${r.matches.length} dòng)`}>
            {/* File có thể lặp mã giao dịch: khoá dòng theo vị trí */}
            <Table rowKey={m => `${m.row.ref}-${r.matches.indexOf(m)}`} size="middle" columns={columns} dataSource={r.matches} pagination={false} scroll={{ x: 1000 }} />
          </Panel>
          {r.missing.length > 0 && <MissingList items={r.missing} note="Bill không có mã giao dịch sẽ không khớp được, nên quầy phải bổ sung mã trước khi khóa sổ." />}
        </>
      )}
    </div>
  );
}

function BankTab() {
  const s = useKt();
  const r = matchBank(s.bank, s.bills);
  const columns: ColumnsType<BankMatch> = [
    { title: 'Ngày', key: 'd', width: 110, render: (_, m) => (m.row.date ? dmy(m.row.date) : '—') },
    { title: 'Nội dung chuyển khoản', key: 'desc', render: (_, m) => <span className="text-gray-700 dark:text-gray-300">{m.row.desc}</span> },
    { title: 'Bill, khách', key: 'b', render: (_, m) => (m.bill ? `${m.bill.no} · ${m.bill.customer}` : <span className="text-gray-400">Không tìm thấy</span>) },
    { title: 'Khớp theo', key: 'by', width: 120, render: (_, m) => (m.by === 'ref' ? 'Mã giao dịch' : m.by === 'bill' ? 'Số bill' : '—') },
    { title: 'Số tiền', key: 'a', align: 'right', width: 130, render: (_, m) => <b>{fmtMoney(m.row.amount)}</b> },
    { title: 'Kết quả', key: 's', width: 170, render: (_, m) => <MatchTag status={m.status} label={MATCH_LABEL[m.status]} /> },
  ];
  const matched = r.matches.filter(m => m.status === 'matched').length;
  return (
    <div className="flex flex-col gap-4">
      <FileButtons hasData={s.bank.length > 0} columns="Ngày, Ghi có, Nội dung"
        onSample={() => ktApi.setBank(sampleBank(s))} onClear={() => ktApi.setBank([])}
        onRows={rows => { const parsed = parseBank(rows); if (parsed.length) ktApi.setBank(parsed); return parsed.length; }} />
      {s.bank.length > 0 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Kpi icon={<CheckCircle2 size={20} />} tone="bg-emerald-50 text-emerald-600" label="Tự khớp" value={`${matched}/${r.matches.length}`} hint="Đọc mã giao dịch hoặc số bill trong nội dung" />
            <Kpi icon={<FileQuestion size={20} />} tone="bg-amber-50 text-amber-600" label="Cần xem tay" value={r.matches.length - matched} hint="Tiền vào chưa biết của bill nào" />
            <Kpi icon={<Landmark size={20} />} tone="bg-sky-50 text-sky-600" label="Tổng tiền vào" value={fmtMoney(r.total)} />
          </div>
          <Panel title={`Kết quả khớp (${r.matches.length} dòng)`}>
            <Table rowKey={m => `${m.row.date}${m.row.desc}-${r.matches.indexOf(m)}`} size="middle" columns={columns} dataSource={r.matches} pagination={false} scroll={{ x: 900 }} />
          </Panel>
          {r.missing.length > 0 && <MissingList items={r.missing} note="Bill báo đã chuyển khoản nhưng chưa thấy tiền về trong sao kê." />}
        </>
      )}
    </div>
  );
}

/** Đối soát Payoo và sao kê ngân hàng (SRS 5.1, 5.2) */
export default function KeToanReconcile() {
  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Đối soát" desc="Nạp file Payoo hoặc sao kê, hệ thống tự khớp với bill và tách phí cà thẻ" />
        <DemoBar />
        <Tabs items={[
          { key: 'payoo', label: 'Cổng Payoo', children: <PayooTab /> },
          { key: 'bank', label: 'Sao kê ngân hàng', children: <BankTab /> },
        ]} />
      </div>
    </Layout>
  );
}
